/* eslint-disable react/display-name */
import { AnchorButton, Classes, Intent, Tag } from '@blueprintjs/core';
import * as React from 'react';
import { QueryBuilderCopy, Text } from 'vet-bones/components';

import { MAX_CSV_UPLOAD_BYTES } from 'src/constants/limits';
import {
  PROJECT_SETTINGS_DOCUMENTATION_LINK,
  SSL_SETUP_DOCUMENTATION_LINK,
} from 'src/constants/links';
import { ProjectSidebarTileId } from 'src/constants/mosaic';
import {
  ProjectPropertyType,
  ProjectResourceType,
} from 'src/constants/project';
import { STARDOG_FEATURE_SUPPORT } from 'src/store/features/types';
import { VirtualGraphOption } from 'src/store/state/dataSources';
import { formatDateFromMs } from 'src/utils/formatDateFromMs';

const rulesQueryBuilderBase: Omit<QueryBuilderCopy, 'error'> = {
  body: {
    expression: {
      duplicate: 'Duplicate',
      remove: 'Remove',
    },
    conjunction: {
      andNot: 'AND NOT',
      or: 'OR',
    },
    class: {
      anyLabel: 'Any Class',
      placeholder: 'Select class...',
      existingInstance: 'Existing instance',
      newInstance: 'New instance',
    },
    input: {
      booleanFalse: 'False',
      booleanTrue: 'True',
      datePlaceholder: 'Select date',
      dateRangePlaceholder: 'Select range',
      numberPlaceholder: 'Enter number...',
    },
    instance: {
      createNew: 'Use ',
      valueTooltip:
        'To specify an instance, provide a set of valid IRIs enclosed in backticks ([`]) and comma-separated ([,])',
    },
    property: {
      attributes: 'Attributes',
      classify: 'Classify As',
      instance: 'In',
      label: 'Label',
      relationships: 'Relationships',
      placeholder: 'Select property...',
    },
    operator: {
      comparison: 'Comparison',
      placeholder: 'Set value',
    },
  },
};

export const rulesIfQueryBuilder: QueryBuilderCopy = {
  ...rulesQueryBuilderBase,
  error: {
    classInstanceDefined:
      'Class instance is already defined. To continue with this rule, select a different class instance or property.',
    classModelDomainOrRange:
      'Class instances must be associated to the selected property. To continue with this rule, select a valid class instance.',
    classModelUndefined:
      'Class instances must be defined in the model. To continue with this rule, select an existing class instance.',
    classVariableInvalid:
      'Class instances cannot be associated to instances in other AND NOT expressions. To continue with this rule, select a different class instance.',
    // not used for if block
    classPreviousInstance: '',
    classSubjectConnected:
      'Class instances must be associated with previous instances. To continue with this rule, connect this class instance to a previous instance.',
    instanceValueBackticks: 'Valid IRIs must be enclosed in backticks ([`]).',
    instanceValueCharacters: (chars: string[]) =>
      `The IRI is invalid. "${chars.join(',')}" is not allowed.`,
    instanceValueSemicolon: 'The IRI must include a semicolon ([:]).',
    instanceValueSplitCharacter: 'Valid IRIs must be comma separated ([,]).',
    instanceValueStartsWith:
      'The IRI must start with an alphabet character ([A-Za-z]).',
    propertyModelUndefined:
      'Properties must be defined in the model. To continue with this rule, select an existing property.',
    propertyModelDomainOrRange:
      'Properties must be associated with the selected class. To continue with this rule, select a valid property.',
  },
};

export const rulesThenQueryBuilder: QueryBuilderCopy = {
  ...rulesQueryBuilderBase,
  error: {
    classInstanceDefined:
      'Class instance is already defined. To continue with this search, select a different class instance or property.',
    classModelDomainOrRange:
      'Class instances must be associated to the selected property. To continue with this rule, select a valid class instance.',
    classModelUndefined:
      'Class instances must be defined in the model. To continue with this rule, select an existing class instance.',
    classPreviousInstance:
      'Class instances must be defined in the "If" clause. To continue with this rule, select an existing class instance.',
    classSubjectConnected:
      'Class instances must be associated with previous instances. To continue with this rule, connect this class instance to a previous instance.',
    // not used for then block
    classVariableInvalid: '',
    instanceValueBackticks: 'Valid IRIs must be enclosed in backticks ([`]).',
    instanceValueCharacters: (chars: string[]) =>
      `The IRI is invalid. "${chars.join(',')}" is not allowed.`,
    instanceValueSemicolon: 'The IRI must include a semicolon ([:]).',
    instanceValueStartsWith:
      'The IRI must start with an alphabet character ([A-Za-z]).',
    instanceValueSplitCharacter: 'Valid IRIs must be comma separated ([,]).',
    propertyModelUndefined:
      'Properties must be defined in the model. To continue with this rule, select an existing property.',
    propertyModelDomainOrRange:
      'Properties must be associated with the selected class. To continue with this rule, select a valid property.',
  },
};

export const components = {
  defaults: {
    advancedOptions: 'advanced options',
    alert: {
      ok: 'OK',
    },
    backButton: {
      back: 'Back',
    },
    confirmation: {
      title: 'Are you sure?',
      no: 'No',
      yes: 'Yes',
    },
    error: 'Something went wrong.',
    filter: 'Filter...',
    filterOrCreate: 'Filter or create...',
    itemSelectorCreate: (query: string) => `Create "${query}"`,
    noResults: 'No results.',
    prompt: {
      submit: 'OK',
      cancel: 'Cancel',
    },
  },
  appNavbar: {
    title: 'Stardog Designer',
  },
  cards: {
    connection: {
      label: 'Connect to Stardog',
      placeholder: '',
    },
    model: {
      label: 'Select a model',
      placeholder: 'Select a model',
      selected: (models: string[]) =>
        (models.length > 1 ? `${models.length} selected` : models[0]) || '',
    },
  },
  classSelector: {
    placeholder: 'Select class(es)...',
    singularPlaceholder: 'Select a class...',
  },
  colorPicker: {
    title: 'Color',
    tooltip: 'Change color',
  },
  databaseSelector: {
    placeholder: 'Select a database',
    creatablePlaceholder: 'Select or create',
    errorMessages: {
      startsWith:
        'The database name must start with an alphabet character ([A-Za-z]).',
      catalog:
        'This database already exists. Please enter a unique database name.',
      characters: (chars: string[]) =>
        `The database name is invalid. "${chars.join(',')}" is not allowed.`,
    },
  },
  dropzone: {
    browse: 'Browse',
    drag: 'Drag files to upload',
  },
  genericPropertiesEditor: {
    validation: {
      duplicatedNameTooltip: 'Options may only be specified once',
      reservedNameTooltip: 'This is a reserved option that can be set above',
      missingValueTooltip: 'A value is required',
    },
  },
  home: {
    search: 'Search projects',
    new: 'New',
    newProject: 'New Project',
    importProject: 'Import project',
    orderBy: 'Order by',
    cards: {
      deleteMenuItem: 'Delete Project',
      duplicateMenuItem: 'Duplicate Project',
    },
    voicebox: {
      headerFirst: 'Introducing',
      headerSecond: 'Stardog Voicebox',
      body: "Voicebox is Stardog's LLM for accelerating the development, maintenance, and use of a knowledge graph. Strike up a conversation about what type of data model you want built and Voicebox will make it happen!",
      button: 'Start the conversation',
    },
    noProjects: 'No Projects',
    noProjectsText: (
      <Text className={Classes.TEXT_LARGE}>
        You have not created any projects, click on{' '}
        <strong>&quot;New Project&quot;</strong> to get started
      </Text>
    ),
  },
  createProjectDialog: {
    title: 'New Project',
    name: 'Project name',
    namePlaceholder: 'Enter project name',
    select: 'Select how to start your project',
    voiceboxTitle: 'Voicebox',
    voiceboxDescription: 'Build via Voicebox',
    blankTitle: 'Blank Canvas',
    blankDescription: 'Build from scratch',
    importTitle: 'Import a model',
    importDescription: 'Import an existing model to edit, extend, or map to',
    resourceTitle: 'Connect a resource',
    resourceDescription:
      'Leverage technical metadata from data connections to map and model',
    validation: {
      name: 'Name is required',
    },
  },
  fileUploadPreviewDialog: {
    title: 'File Upload Preview',
  },
  invalidMappings: {
    dialog: {
      cancel: 'Cancel',
      message:
        'The following mappings will not be included as they do not include a valid primary identifier',
      proceed: 'Proceed',
      title: 'Invalid mappings',
    },
    validation: {
      primaryIdentifier: (name: string) =>
        `mapping "${name}" does not have a valid "primaryIdentifier"`,
      sourcePrimaryIdentifier: (name: string) =>
        `mapping "${name}" does not have a valid "sourcePrimaryIdentifier"`,
      targetPrimaryIdentifier: (name: string) =>
        `mapping "${name}" does not have a valid "targetPrimaryIdentifier"`,
      to: (name: string) => `mapping "${name}" has no "to"`,
    },
  },
  project: {
    resources: {
      title: 'Project Resources',
      filter: 'Search resources...',
      delete: 'Delete Resource',
      viewPreview: 'View Preview',
    },
    models: {
      title: 'Models',
      filter: 'Search models...',
    },
    navbar: {
      export: 'Export',
      exportHover: 'Export model, mapping, and project definition files',
      mapping: 'Mapping',
      mappingHover: 'Toggle mapping view',
      model: 'Model',
      modelHover: 'Toggle model view',
      publish: 'Publish',
      publishHover: 'Publish to Stardog Endpoint',
      settings: 'Settings',
    },
    propertyType: {
      buttonText: (type: ProjectPropertyType) => {
        const types: Record<ProjectPropertyType, string> = {
          RDFS: 'Yes',
          SCHEMA_ORG: 'No',
          CUSTOM: 'Custom per property',
        };
        return types[type];
      },
      info: (
        <>
          Shared properties (relationships and attributes) can imply inference
          class types.{' '}
          <div className="sd-tooltip-footer">
            <AnchorButton
              href={PROJECT_SETTINGS_DOCUMENTATION_LINK}
              intent={Intent.PRIMARY}
              outlined
              rel="noreferrer"
              small
              target="_blank"
            >
              Learn more
            </AnchorButton>
          </div>
        </>
      ),
      label: 'Infer class types based on shared properties',
    },
    resourceTypes: {
      fileUpload: 'file upload',
      virtualGraph: 'virtual graph',
      model: 'model',
    },
  },
  projectDiagram: {
    attributeDrawer: {
      title: 'Attribute Detail',
      name: 'Attribute Name',
      dataType: 'Data Type',
      associations: 'Associations',
      sensitivePropertyGroups: 'Sensitive Property Groups',
      createSPG: 'Create',
      filter: 'Filter',
      nameColumn: 'Name',
      attributeColumn: 'Other attributes',
      moreCount: (count: number) =>
        count > 2 ? `, and ${count - 2} more` : '',
    },
    attributesTable: {
      createRule: 'Create',
      label: 'Attributes',
      missingAttribute: 'Attribute is required',
      missingDatatypes: 'Data type(s) is required',
      missingName: 'Attribute name is required',
      nameColumn: 'Attribute Name',
      noType: 'No type',
      rule: 'Rule',
      typeColumn: 'Data Type',
      updateButton: 'Update',
    },
    classDrawer: {
      createTitle: 'Create Class',
      title: 'Class Detail',
      name: 'Class Name',
      nameWithAttributes: 'Class Name - Attributes',
      description: 'Description',
      childClasses: 'Child Classes',
      parentClasses: 'Parent Classes',
      relationshipsTable: {
        label: 'Relationships',
        missingRelationship: 'Relationship is required',
        missingDirection: 'Direction is required',
        missingName: 'Relationship name is required',
        nameColumn: 'Relationship Name',
        directionColumn: 'Direction',
        directionIncoming: 'Incoming',
        directionOutgoing: 'Outgoing',
      },
    },
    contextMenu: {
      delete: 'Delete',
      deleteResource: 'Delete resource',
      deleteMultiple: 'Delete objects',
      edit: 'Edit',
      mapping: 'Mapping',
      mappings: 'Mappings',
      preview: 'View preview',
      seeDetails: 'See details',
    },
    createDialogs: {
      createButton: 'Create',
      errors: {
        noName: 'A name is required.',
        notUnique:
          'The name you have entered is not unique in your project. Please enter a unique name.',
      },
    },
    createDrawers: {
      cancelButton: 'Cancel',
      createButton: 'Create',
    },
    editableIri: {
      showIri: 'Show IRI',
      hideIri: 'Hide IRI',
      modelIriNamespace: '[MODEL NAMESPACE]',
      modelIriPlaceholder: '[MODEL NAMESPACE]modelConcept...',
      validationLabelRequired: 'Name is required.',
      validationIriRequired: 'IRI is required.',
      validationIriStartsWith:
        'The IRI must start with an alphabet character ([A-Za-z]).',
      validationBaseIriSemicolon:
        'The base IRI must end with a semicolon ([:]).',
      validationIriSemicolon: 'The IRI must include a semicolon ([:]).',
      validationIriCharacters: (chars: string[]) =>
        `The IRI is invalid. "${chars.join(',')}" is not allowed.`,
      validationIriUnique:
        'This IRI already exists. Please enter a unique IRI.',
      vgNameValidationError:
        'Data Source names must be unique and start with an alpha character followed by alphanumeric characters, underscores, or dashes.',
    },
    entityDrawerButtons: {
      add: 'Add',
      edit: 'Edit',
      unlink: 'Unlink',
      unlinkDisabled:
        'Inherited attributes cannot be unlinked. The attribute must be unlinked from the parent class.',
      update: 'Update',
      remove: 'Delete',
    },

    mapping: {
      createMessage: 'Mapping name',
      defaultLabel: (idx: number) => `Mapping ${idx + 1}`,
      new: 'New Mapping',
      mappingTarget: 'Mapping target',
      mappingTargetHelp: 'Leave blank to manually connect mapping link',
    },
    modelConceptAttribute: {
      createMessage: 'Attribute name',
      new: 'New Attribute',
    },
    modelConceptClass: {
      createMessage: 'Class name',
      new: 'New Class',
    },
    modelConceptRelationship: {
      createMessage: 'Relationship name',
      new: 'New Relationship',
    },
    modificationWarning: {
      attributesMessage: (className: string) =>
        `Deleting ${className} will also delete the following attributes:`,
      cancel: 'Cancel',
      rulesMessage:
        'Making this modification will cause the following rule(s) to be incomplete:',
      proceed: 'Proceed',
    },
    navbar: {
      addClass: 'Add class',
      addMapping: 'Add mapping',
      addRelationship: 'Add relationship',
      deleteSelection: 'Delete selection',
      redo: 'Redo',
      rules: 'Rules',
      undo: 'Undo',
      voicebox: 'Voicebox',
    },
    relationshipDrawer: {
      createTitle: 'Create Relationship',
      title: 'Relationship Detail',
      name: 'Relationship Name',
      domain: 'Source',
      range: 'Target',
    },
    resourceDrawer: {
      fieldName: 'Field Name',
      fields: 'Fields',
      mapping: 'Mapping',
      mappingName: 'Mapping Name',
      newMapping: 'New Mapping',
      removeMapping: 'Remove Mapping',
      target: 'Target',
      title: 'Resource Detail',
      type: 'Type',
      updateDate: 'Update Date',
    },
    sensitivePropertyGroupDrawer: {
      createTitle: 'Create Sensitive Property Group',
      title: 'Sensitive Property Group Detail',
      name: 'Name',
      new: 'New Sensitive Property Group',
      attributes: 'Attributes',
      validation: {
        name: {
          required: 'Name is required',
          characters: (chars: string[]) =>
            `The sensitive property group name is invalid. "${chars.join(
              ','
            )}" is not allowed.`,
          unique:
            'This sensitive property group already exists. Please enter a unique name.',
        },
      },
    },
  },
  projectMapping: {
    emptyState: {
      title: 'No mapping selected',
      description: 'Select a mapping from the sidebar on the left',
    },
    sidebar: {
      collapseAll: 'Collapse All',
      expandAll: 'Expand All',
      newMapping: 'New Mapping',
      resourceSuggestionsDisabled: `Resource suggestions require Stardog Knowledge Graph endpoint version ${STARDOG_FEATURE_SUPPORT.autoMappingMultipleTables.minVersion}+`,
    },
    annotations: {
      button: 'Annotations',
      label: 'Label',
      labelHelp:
        'Label annotations are used throughout Stardog as the display name for an instance. When no label is set, the primary identifier value will be displayed instead.',
    },
    attributes: {
      edit: 'Edit Attributes',
      inheritedFrom: 'Inherited from',
      section: 'Attribute',
    },
    filter: {
      directAttributes: 'Attributes',
      inheritedAttributes: 'Inherited Attributes',
      directRelationships: 'Relationships',
      inheritedRelationships: 'Inherited Relationships',
      iconLabel: 'Filter Attributes',
      selectAll: 'Select All',
      clear: 'Clear',
    },
    menu: {
      duplicateMapping: 'Duplicate mapping',
      removeMapping: 'Remove mapping',
    },
    primaryIdentifier: {
      label: 'Primary Identifier',
      help: 'A primary identifier is used to uniquely define entities for your mapping target. If a mapping target has entities from multiple sources, the primary identifiers across sources should match to avoid duplicates.',
      fieldRequired:
        ' Primary identifiers require at least one field to be used',
      inputPlaceholder: 'Field or static text',
      invalidCharacter: 'Special characters are not supported',
      invalidSpace: 'Spaces are not supported',
      preview: 'Preview: ',
      emptyPreview: 'Preview will be shown once at least one field is selected',
      staticText: (query: string) => `Static Text "${query}"`,
      showIri: 'Show base IRI',
      hideIri: 'Hide base IRI',
      editedIri: 'An edited base IRI cannot be hidden',
      dataIriNamespace: '[INSTANCE DATA NAMESPACE]',
      dataIriPlaceholder: (classLocalName: string) =>
        `[INSTANCE DATA NAMESPACE]${classLocalName}:`,
    },
    relationships: {
      classSelector: (isSource: boolean) =>
        `${isSource ? 'Source' : 'Target'} Class`,
      identifierType: (isSource: boolean) =>
        `${isSource ? 'Source' : 'Target'} Identifier`,
      inheritedFrom: 'Inherited from',
      mapping: 'Mapping',
      mappedIn: 'mapped in',
      alsoMappedIn: `Also mapped in`,
      newMapping: 'New mapping',
      noMappingItems: 'There are no existing mappings for this class.',
      section: 'Relationships',
      source: 'Source',
      target: 'Target',
      sourceWarning:
        'The field used to denote the primary identifier for the source class differs from the field used to denote the primary identifier for this same class from the same project resource. Typically the same identifier is used.',
      targetWarning:
        'The field used to denote the primary identifier for the target class differs from the field used to denote the primary identifier for this same class from the same project resource. Typically the same identifier is used.',
    },
    resourceTypeLabel: (type?: ProjectResourceType) => {
      if (!type) {
        return '';
      }

      const displayType =
        type === ProjectResourceType.FILE_UPLOAD
          ? 'Type: CSV'
          : 'Type: Virtual Graph';
      return displayType;
    },
    suggestions: {
      button: 'Suggest Mapping',
      disabled:
        'Suggestions require Stardog Knowledge Graph endpoint version 8.1.0+',
      score: 'Score',
      action: 'Action',
      low: 'LOW',
      medium: 'MEDIUM',
      high: 'HIGH',
    },
  },
  projectModelDialog: {
    title: 'Add a model',
    addButton: 'Add',
    cancelButton: 'Cancel',
  },
  projectResourceDialog: {
    title: 'Add a new project resource',
    backButton: 'Back',
    createAndMapButton: 'Create & Map',
    createButton: 'Create',
    dataSourceTypeSelectionPanel: {
      noDataSources: 'No data sources have been added to this Stardog endpoint',
    },
    resourceTypePanel: {
      description:
        'Upload a file, or, to create a new virtual graph, confirm a endpoint to get started.',
      dropzone: {
        title: 'File Upload',
        drag: 'Drag and drop a CSV here to upload',
        button: 'Browse Files',
      },
      connection: {
        title: 'New Virtual Graph',
        proceedButton: 'Proceed',
        username: (uname?: string) => `username: ${uname || ''}`,
        otherConnection: 'Want to use a different endpoint? ',
        selectHere: 'Select here',
        notConnected:
          'Your project is not currently connected to a Stardog database',
      },
    },
    projectDataSourcePanel: {
      endpoint: 'Endpoint',
      username: 'Username',
      createNewDataSource: 'Create new Data Source',
    },
    projectDataSourceCreationPanel: {
      advancedOptions: (count: number) => `Advanced Options (${count})`,
      dataSourceNameLabel: 'Data Source Name',
      invalidDataSourceProperties: (names: string[]) =>
        names.find((name) => name === '') !== undefined
          ? 'Property keys cannot be blank.'
          : `Invalid values for properties: ${names.join(', ')}`,
      missingDataSourceProperties: (names: string[]) =>
        `Missing values for properties: ${names.join(', ')}`,
      name: {
        required: 'Data source name is required',
        startsWith:
          'The data source name must start with an alphabet character ([A-Za-z]).',
        characters: (chars: string[]) =>
          `The data source name is invalid. "${chars.join(
            ','
          )}" is not allowed.`,
        unique:
          'This data source already exists. Please enter a unique data source name.',
      },
      propertyLabels: {
        ENDPOINT: 'Endpoint',
        USERNAME: 'Username',
        PASSWORD: 'Password',
      },
    },
    projectFileUploadPanel: {
      form: {
        duplicateHeaders:
          'The uploaded file contains duplicate values in the first row. Using headers is not supported for duplicate values.',
        escape: 'Escape character',
        header: 'Has header',
        name: 'Resource name',
        path: 'Source',
        preview: 'Preview',
        previewRowCount: (rowCount: number, allResultsShown: boolean) =>
          allResultsShown ? `${rowCount} records` : `${rowCount} samples`,
        resourceProperties: 'Resource Properties',
        quote: 'Quote character',
        separator: 'Separator character',
        skipEmpty: 'Skip empty values',
      },
      errors: {
        empty: 'No data',
        details: 'Details',
        title: 'Failed to parse row:',
        row: 'Row',
      },
      statistics: {
        distinct: (value: number) =>
          `${value % 1 === 0 ? value : value.toFixed(2)}% distinct`,
        records: (value: number) => `${value} record${value === 1 ? '' : 's'}`,
      },
      validation: {
        empty: 'Cannot upload CSV without data',
        errors: 'Cannot upload CSV with errors',
        escape: 'Escape character is required',
        name: 'Data Source name is required',
        quote: 'Quote character is required',
        separator: 'Separator character is required',
      },
    },
    projectVirtualGraphPanel: {
      form: {
        runQuery: 'Run',
        name: 'Resource name',
        pasteQuery: 'Paste query',
        preview: 'Preview',
        previewRowCount: (rowCount: number, allResultsShown: boolean) =>
          allResultsShown ? `${rowCount} records` : `${rowCount} samples`,
        resourceScope: 'Resource Scope',
        resourceProperties: 'Resource Properties',
        tablesTooltip: {
          noTables:
            'There are no tables to select from. Contact your system admin to ensure the data source metadata is up to date.',
          noPermissions:
            'You are not authorized to see data source metadata. Contact your system admin to ensure you have the correct user privileges.',
        },
      },
      customQuery: 'Build custom query',
      selectTable: 'Select table(s)',
      preview: {
        clickToConnect: 'Connect and refresh preview',
        clickToRefresh: 'Refresh preview',
        errors: {
          badQueryOneError:
            'An error was encountered while attempting to query the data source',
          badQueryMultipleErrors:
            'Multiple errors were encountered while attempting to query the data source',
          badQueryDetails: 'Details',
          emptyRefresh:
            'Attempting to refresh returned no results; retaining data from last update',
          previewEmpty:
            'There are no cached results for this resource. Refresh to view the data preview. The preview will remain empty if the resource scope has no data.',
        },
        lastUpdated: (timestamp: number) =>
          `Last updated: ${formatDateFromMs(timestamp)}`,
        validateToPreview: 'Run query to see preview',
        selectToPreview: 'Select a table to see a preview',
      },
      resourceProperties: {
        [VirtualGraphOption.SQL_QUOTING_CONVENTION]: {
          label: 'SQL Quoting Convention',
          description:
            'If set to `NATIVE`, R2RML views (using `rr:sqlQuery`) will be parsed using the DB-native identifier quoting convention. For example, MySQL queries will be parsed treating backtick as the identifier quote character. If set to `ANSI`, the ANSI SQL convention of treating a double quote as the identifier quote character will be used instead.',
        },
        [VirtualGraphOption.SQL_FUNCTIONS]: {
          label: 'SQL Function Names',
          description:
            'A comma-separated list of SQL function names to register with the parser. If an R2RML view (using `rr:sqlQuery`) fails to parse, this option can be set to allow use of non-standard functions.',
        },
        [VirtualGraphOption.PERCENT_ENCODE]: {
          label: 'Percent Encode IRI Template Strings',
          description:
            'Should IRI template strings be percent-encoded to be valid IRIs?',
        },
        advancedProperties: (count: number) => `Advanced Properties (${count})`,
      },
      validation: {
        emptyQuery:
          'Cannot create a Virtual Graph without a selected table or validated custom query',
        runningQuery: 'Cannot create a Virtual Graph while a query is running',
        errors: 'Cannot create a Virtual Graph with query errors',
        tableErrors: (tables: string[]) =>
          `Cannot create a Virtual Graph with query errors: ${tables.join(
            ', '
          )}`,
        invalidResourceProperties:
          'Cannot create a Virtual Graph with invalid properties',
        name: 'Resource name is required',
        names: (tables: string[]) =>
          `Resource names are required: ${tables.join(', ')}`,
      },
    },
  },
  projectRuleDetailDrawer: {
    title: 'Rule Detail',
    name: 'Rule Name',
    newRule: 'New Rule',
    ifClause: 'When the following conditions are met:',
    thenClause: 'Then infer:',
    classifyProperty: 'Classify As',
    delete: 'Delete',
    cancel: 'Cancel',
    reasoningWarning:
      'Your rule uses patterns that are only available when enabling the Stride reasoner. If the Stride reasoner is not enabled, this rule will be ignored.',
    submit: 'Save',
    validation: {
      nameRequired: 'Rule name is required',
      incomplete: 'Please complete the highlighted criteria',
    },
  },
  projectRuleOverviewDrawer: {
    emptyState: {
      description:
        'Define inference rules to define capture business and domain rules in your model.',
      title: 'Inference Rules for explainable AI',
    },
    title: 'Inference Rules',
    createRule: 'Create Rule',
    invalidRules: 'Incomplete Rules',
    invalidRule: 'Incomplete Rule',
  },
  projectSettingsDrawer: {
    title: 'Project Settings',
    name: 'Project Name',
    modelNamespace: 'Model Namespace',
    dataNamespace: 'Instance Data Namespace',
    useAdvancedSettings: 'Customize advanced project details',
    customPropertyTypeWarning:
      'This change will modify your model and overwrite your existing property settings.',
    cancel: 'Cancel',
    submit: 'Save',
    validation: {
      nameRequired: 'Project name is required',
      namespaceRequired: 'Namespace is required',
      namespaceStartsWith:
        'The IRI must start with an alphabet character ([A-Za-z]).',
      namespaceSemicolon: 'The IRI must include a semicolon ([:]).',
      namespaceChars: (chars: string[]) =>
        `The IRI is invalid. "${chars.join(',')}" is not allowed.`,
    },
  },
  publishProjectDrawer: {
    title: 'Publish Project to Stardog',
    newTitle: 'Publish Project',
    connectionDetails: {
      title: 'Endpoint Details',
      help: 'Confirm the Stardog endpoint and database you want to publish your project to.',
    },
    connectionConfiguration: {
      header: 'Endpoint and Database',
      singleConnectionHeader: 'Database',
      serverSectionHeader: 'Stardog Endpoint',
      databaseSectionHeader: 'Database',
      namespaceSection: {
        header: 'Add Model Namespace',
        iri: 'IRI',
        iriFieldWarning:
          'Published namespaces allow users to abbreviate IRIs by using a prefix. The suggested IRI is based on the model namespace for your project. Changing the IRI could reduce the relevancy of the published prefix.',
        overwriteWarningMessage: (prefix: string, existingIri: string) => (
          <>
            The{' '}
            <Tag intent={Intent.WARNING} minimal>
              {prefix}
            </Tag>{' '}
            prefix is already associated with the{' '}
            <Tag intent={Intent.WARNING} minimal>
              {existingIri}
            </Tag>{' '}
            IRI in the selected database. Proceeding with publish will replace
            the current namespace.
          </>
        ),
        overwriteWarningTitle: 'Existing namespace will be overwritten',
        prefix: 'Prefix',
        prefixEmpty: '<empty>',
      },
    },
    mappingConfiguration: {
      header: 'Configure Mappings',
      columnHeader: {
        resource: 'Resource',
        mappings: 'Mappings',
        configuration: 'Configuration',
      },
      advancedResourceSwitch: 'Customize advanced resource details',
      virtualGraph: {
        label: 'Virtual graph',
        create: 'Create new',
        replace: 'Replace',
        select: 'Select an existing virtual graph',
        placeholder: 'Select a virtual graph',
        versionWarning:
          'Resource mappings for Stardog Knowledge Graph endpoints of higher versions than the Stardog endpoint version being published may not be compatible.',
      },
      namedGraph: {
        append: 'Append',
        label: 'Named graph',
        create: 'Create new',
        replace: 'Replace',
        placeholder: 'Select a named graph',
      },
      csvUpload: {
        label: 'Re-upload large CSV resource',
        info: `Designer only stores files under ${
          MAX_CSV_UPLOAD_BYTES / 1024 / 1024
        } MB. To publish your project, re-upload this resource.`,
      },
    },
    modelConfiguration: {
      header: 'Configure Model',
      modelSelection: 'Model Configuration',
      advancedModelSwitch: 'Customize Advanced Model Details',
      modelName: {
        label: 'Model name',
        create: 'Create new model',
        replace: 'Replace an existing model',
        select: 'Select an existing model',
        placeholder: 'Select a model',
      },
      namedGraph: {
        label: 'Named graph',
      },
      rules: {
        label: 'Publish Inference Rules',
        incompleteRulesWarning: 'Incomplete rules will not be published.',
      },
      sensitivePropertyGroups: {
        label: 'Publish Sensitive Properties',
        groupLabel: 'Sensitive Property Group',
        configureLabel: 'Configure',
        new: 'Create new',
        replace: 'Replace',
        append: 'Append',
        existingPlaceholder: 'Select an existing group',
        newPlaceholder: 'Enter a new group name',
        duplicateGroup: (group: string) =>
          `The group ${group} already exists for your database. Please enter a unique name.`,
        duplicatePublishGroup: (group: string) =>
          `The group ${group} is already being published. Please enter a unique name.`,
        replaceOrAppendUnnamedGroup: (group: string) =>
          `The group ${group} needs to be assigned an existing sensitive property group.`,
      },
    },
    openInExplorer: {
      card: {
        label: 'Open in Explorer',
        description:
          'An intuitive interface to visually browse and query your Knowledge Graph.',
        altText: 'Open in Explorer',
      },
      close: 'Close',
      success: 'Success!',
      successMessage:
        'Your project was successfully published. \n\n Ready to see your knowledge graph in action? Open your published work in Explorer.',
    },
    submit: 'Publish',
    upload: {
      drag: 'Drag files to upload',
      label: 'Confirm and upload project files',
    },
    validation: {
      connection: 'Endpoint is required',
      database: 'Database is required',
      refreshDatabase: 'Refreshing database...',
      modelName: {
        required: 'Model is required',
        startsWith: 'Model name must start with [A-Za-z].',
        characters: (chars: string[]) =>
          `The model name is invalid. "${chars.join(',')}" is not allowed.`,
        unique: 'This model already exists. Please enter a unique model name.',
      },
      namespace: 'Namespace is invalid',
      upload: 'All files must be uploaded',
      namedGraph: {
        required: 'Named graph is required.',
        startsWith:
          'The named graph must start with an alphabet character ([A-Za-z]).',
        semicolon: 'The named graph must include a semicolon ([:]).',
        characters: (chars: string[]) =>
          `The named graph is invalid. "${chars.join(',')}" is not allowed.`,
        unique:
          'This named graph already exists. Please enter a unique named graph.',
      },
      csvUpload: {
        incomplete: (name: string, message: string) =>
          `"${name}" is invalid: ${message}`,
        required: 'File upload is required.',
      },
      virtualGraph: {
        incomplete: (name: string, message: string) =>
          `"${name}" is invalid: ${message}`,
        required: 'Virtual graph name is required.',
        startsWith:
          'The virtual graph name must start with an alphabet character ([A-Za-z]).',
        characters: (chars: string[]) =>
          `The virtual graph name is invalid. "${chars.join(
            ','
          )}" is not allowed.`,
        unique:
          'This virtual graph already exists. Please enter a unique virtual graph name.',
      },
    },
    footer: {
      exportProject: 'Download zipped project when publishing',
      next: 'Next',
      publish: 'Publish',
      back: 'Back',
      cancel: 'Cancel',
    },
  },
  resourceSelector: {
    placeholder: 'Select resource(s)...',
  },
  resourceSuggestionsDialog: {
    footer: {
      back: 'Back',
      cancel: 'Cancel',
      create: 'Create',
      next: 'Next',
    },
    selection: {
      classesHelperText:
        'Leave blank if you want Designer to suggest a model for you.',
      classesLabel: 'Select classes to map to',
      invalid: 'A resource must be selected to continue',
      noSuggestions: (hasIgnored: boolean) =>
        `There were no suggestions found.${
          hasIgnored ? ' Suggestions for existing mappings were ignored.' : ''
        } To manually create a mapping select a single resource and class.`,
      resourcesLabel: 'Select resources to map from',
      title: 'New Mapping',
    },
    suggestions: {
      accept: 'Accept',
      acceptDisabled: (x: number) =>
        `Selected suggestion${x === 1 ? '' : 's'} already accepted`,
      collapseAll: 'Collapse All',
      expandAll: 'Expand All',
      inheritedFrom: 'Inherited from',
      label: 'Label',
      reject: 'Reject',
      rejectDisabled: (x: number) =>
        `Selected suggestion${x === 1 ? '' : 's'} already rejected`,
      removeMapping: 'Remove mapping',
      relationshipSuggestionOpposingClass: (
        isOutgoing: boolean,
        classLabel: string
      ) => `${isOutgoing ? 'Target' : 'Source'} Class: ${classLabel}`,
      title: 'Mapping Suggestions',
      relationshipSuggestionOpposingIdentifier: (isOutgoing: boolean) =>
        `${isOutgoing ? 'Target' : 'Source'} Identifier:`,
      xSuggestions: (x: number) => `${x} suggestion${x !== 1 ? 's' : ''}`,
    },
  },
  signInDialog: {
    title: 'Sign in',
    submit: 'Connect',
    form: {
      username: 'Username',
      password: 'Password',
      passwordRequired: 'Password is required',
    },
  },
  virtualGraphPreviewDialog: {
    title: 'Virtual Graph Preview',
  },
  voicebox: {
    title: 'Voicebox',
    disabledFor: {
      [ProjectSidebarTileId.MODELS]:
        'To add models, please first accept or discard the changes to your project.',
      [ProjectSidebarTileId.RESOURCES]:
        'To add resources, please first accept or discard the changes to your project.',
      export:
        'To export, please first accept or discard the changes to your project.',
      publish:
        'To publish, please first accept or discard the changes to your project.',
    },
    disabledReasons: {
      databasesRefreshing: 'refreshing databases',
      messagePending: 'processing prompt',
      stardogConnecting: 'connecting to stardog server',
      stardogNotConnected: 'not connected to stardog server',
    },
    footer: {
      accept: 'Accept Changes',
      help: 'You are viewing a preview of suggested mappings and data model changes.',
      reject: 'Discard',
    },
    initialUserPrompt:
      'A customer 360 use case as it pertains to improving customer satisfaction.',
  },
  webProtocolWarning: {
    httpToHttps: () => (
      <>
        The Stardog Knowledge Graph endpoint is not responding. This could be
        because:
        <ul>
          <li>Your Knowledge Graph endpoint is not running</li>
          <li>
            You are connecting to a HTTPS endpoint from a website without SSL
          </li>
        </ul>
        Confirm your Knowledge Graph endpoint is running and you have the
        correct URL.
      </>
    ),
    httpsToHttp: () => (
      <>
        The Stardog Knowledge Graph endpoint is not responding. This could be
        because:
        <ul>
          <li>Your Knowledge Graph endpoint is not running</li>
          <li>
            You are connecting to a HTTP endpoint from an SSL enabled website
          </li>
        </ul>
        Confirm your Knowledge Graph endpoint is running and you have the
        correct URL.{' '}
        <a href={SSL_SETUP_DOCUMENTATION_LINK} rel="noreferrer" target="_blank">
          Click here
        </a>{' '}
        to learn how to enable SSL for Stardog.
      </>
    ),
    httpToLocal: () => (
      <>
        The Stardog Knowledge Graph endpoint is not responding. This could be
        because:
        <ul>
          <li>Your Knowledge Graph endpoint is not running</li>
          <li>
            You are connecting to a local endpoint from a website without SSL
          </li>
        </ul>
        Confirm your Knowledge Graph endpoint is running and you have the
        correct URL.
      </>
    ),
  },
};

export const newline = '\n';

export const notifications = {
  defaults: {
    failure:
      "Something went wrong and we're not sure what it is. Contact your system admin to review Stardog Knowledge Graph logs for more details if the issue persists.",
    forbidden: (action: string) =>
      `You are not authorized to ${action}. Contact your system admin to ensure you have the correct user privileges.`,
    undo: 'Undo',
  },
  validateConnection: {
    success: "You've connected to the Stardog endpoint!",
    failure: {
      default:
        'The Stardog Knowledge Graph endpoint is not responding. Please check your server status or endpoint.',
      refused:
        'The connection was refused. Make sure your Stardog endpoint is online and your endpoint is correct.',
      disconnected:
        'Oops! You were disconnected from your Stardog endpoint. Try connecting again.',
      invalid: 'Your username and/or password are incorrect.',
      aborted:
        'The request was aborted. Please check the status of your Stardog endpoint.',
    },
  },
  csv: {
    mismatchedHeaders: (filename: string) =>
      `The headers in ${filename} do not match those of the original file`,
    partial: `Files >${
      MAX_CSV_UPLOAD_BYTES / 1024 / 1024
    } MB will need to be uploaded again when publishing.`,
  },
  database: {
    addData: 'add data',
    beginTransaction: 'begin a transaction',
    clearNamedGraph: 'clear named graph',
    createDatabase: 'create a database',
    createDatabaseFailure: 'Failed to create database',
    commitTransaction: 'commit a transaction',
    importCsv: 'import csv files',
    importNamespaces: 'import database namespaces',
    publishSpgs: 'publish sensitive property groups',
    updateDatabaseDetails: 'update database metadata',
  },
  dataSources: {
    creation: {
      actionDescription: 'create data source',
      failure: 'Failed to create data source',
      success: (name: string) => `Created data source ${name}.`,
    },
    suggestions: {
      actionDescription: 'request mapping suggestions',
      failure: 'Failed to request suggestions',
      noneFound: 'No suggested mappings were found.',
    },
  },
  project: {
    exported: (filename: string) => `Exported project to ${filename}`,
    exportFailure: 'Project failed to be exported.',
    importFromFile: {
      invalid: 'Project failed to be imported.',
    },
    removed: (name: string) => `The project ${name} was deleted`,
    restored: (name: string) => `The project ${name} was restored`,
    openedMissing: (name: string | undefined) =>
      name ? `Project ${name} not found!` : 'Project not found!',
  },
  publishProject: {
    model: {
      create: {
        success: (name: string) => `Created model ${name}`,
        failure: 'Failed to create model',
      },
      replace: {
        success: (name: string) => `Replaced model ${name}`,
        failure: 'Failed to replace model',
      },
    },
    namespaces: {
      success: 'Added model namespaces',
      failure: 'Failed to add model namespaces',
    },
    rules: {
      success: 'Added inferencing rules',
      failure: 'Failed to add inferencing rules',
    },
    sensitivePropertyGroups: {
      success: 'Updated sensitive property groups',
      failure: 'Failed to update sensitive property groups',
    },
    virtualGraph: {
      create: {
        success: (name: string) => `Created virtual graph ${name}`,
        failure: (name: string) => `Failed to create virtual graph ${name}`,
      },
      replace: {
        success: (name: string) => `Replaced virtual graph ${name}`,
        failure: (name: string) => `Failed to replace virtual graph ${name}`,
      },
    },
    importCsv: {
      success: (name: string) => `Imported CSV ${name}`,
      failure: (name: string) => `Failed to import ${name}`,
    },
    fileUploadMetadata: {
      success: 'Added file upload metadata',
      failure: 'Failed to add file upload metadata',
    },
    success: (name: string) => `Published ${name}`,
    partial: (name: string) => `Problems publishing ${name}`,
    failure: (name: string) => `Failed to publish ${name}`,
  },
  rules: {
    saved: (name: string) => `Saved rule ${name}`,
  },
  voicebox: {
    exported: (filename: string) => `Exported conversation to ${filename}`,
    addToProjectFailed:
      'Failed to add Voicebox model to Project. Please try again.',
  },
};

export const requests = {
  noConnection:
    'No connection passed in action or default connection in state not yet setup',
  operationFailed: 'Operation failed.',
};
